/**
 * Lead Scoring settings shell strings (PREC-4055): the tab strip's issue panel,
 * the save bar, the leave guard, and the issue messages the pure issue engine
 * (`Score/shell/scoreIssues.ts`) emits by key. Spread into `leadScoring` via
 * `...enLeadScoringShell`.
 */
export const enLeadScoringShell = {
	matrix: {
		segments: "Segments",
		prospects: "Prospects",
		partners: "Partners",
		investors: "Investors",
		missing: "Missing Lead Scoring",
		missingShort: "Missing",
		editScoringShort: "Edit scoring",
		missingTip:
			"This combination is active but has no Lead Scoring, so the leads are found and enriched and then never graded. Pick a Lead Scoring, or deactivate the persona for this segment.",
		notExisting: "Company/Persona combination does not exist",
		notExistingTip:
			"{{persona}} is not activated for {{segment}}, so nothing is graded here. Picking a Lead Scoring activates the persona for this segment and applies the scoring in one step.",
		pickTitle: "Select a Lead Scoring for this combination",
		editScoring: "Edit this Lead Scoring",
		createScoring: "Create new Lead Scoring",
		clearScoring: "Remove the Lead Scoring from this combination",
		deactivateHere: "Deactivate persona for this company segment",
		deactivateForInvestors: "Deactivate persona for investors",
	},
	overview: {
		intro: "Define which Lead Scoring applies to which combination",
		add: "Add",
		addSegment: "Add company segment",
		addPersona: "Add persona segment",
		missingBanner_one: "{{count}} combination is missing a Lead Scoring.",
		missingBanner_other: "{{count}} combinations are missing a Lead Scoring.",
		missingBannerBody:
			"Those leads are found and enriched but never graded, so they stay unscored in your lists.",
		summaryTitle: "Lead Scoring Summary",
		combinations:
			"Your <b>{{segments}} company segments</b> combined with <b>{{personas}} personas</b> make <b>{{live}} activated combinations</b>.",
		deactivatedCombinations:
			"<b>{{count}} combinations</b> are deactivated, because not all personas exist at all company segments. You can change this in the table above.",
		everyCombination:
			"Technically, every single combination could have its own Lead Scoring. In reality, they often overlap.",
		separateScoring:
			"Especially if you have different research questions, or want to apply different campaigns and workflows, it makes sense to create a separate Lead Scoring.",
		configured_one: "You have <b>{{count}} Lead Scoring</b> configured.",
		configured_other: "You have <b>{{count}} Lead Scorings</b> configured.",
		createAdditional: "Create additional Lead Scoring",
		scored_one: "<b>{{count}} combination</b> gets scored.",
		scored_other: "<b>{{count}} combinations</b> get scored.",
		missing_one: "<b>{{count}} combination</b> is missing a Lead Scoring",
		missing_other: "<b>{{count}} combinations</b> are missing a Lead Scoring",
		appliedTo_one: "applied to <b>{{count}}</b> combination",
		appliedTo_other: "applied to <b>{{count}}</b> combinations",
	},
	general: {
		about: "About {{company}}",
		aboutPlaceholder:
			"What your company does, for whom, and what makes it different. The AI reads this before it judges any lead.",
		competitors: "Competitor description",
		competitorsPlaceholder:
			"Which kinds of companies compete with you, and which only look like they do.",
		competitorsHint:
			"Write a definition here, not names. Named competitors belong on the Competitor List, which flags them wherever they turn up.",
		openCompetitorList: "Open Competitor List",
		disqualifyCompanies: "Disqualify companies",
		disqualifyCompaniesHint:
			"Write definitions here, not names. Individual companies — existing customers, open deals — belong on the Blacklist, which blocks them across every list and campaign.",
		openBlacklist: "Open Blacklist",
		structuredRules: "Structured rules",
		disqualifyPeople: "Disqualify people",
		disqualifyPeopleHint:
			"People matching these rules are disqualified before any persona is considered — for example everyone below a seniority level, or outside your markets.",
		languageHint:
			"The language the AI writes its scoring reasoning and summaries in. It does not change which leads you find.",
	},
	segments: {
		intro: "Describe the companies you sell to",
		subject: "these segments",
		test: "Test segments",
		testTitle: "Test your company segments",
		prospectCount_one: "{{count}} Prospect segment",
		prospectCount_other: "{{count}} Prospect segments",
		partnerCount_one: "{{count}} Partner segment",
		partnerCount_other: "{{count}} Partner segments",
		investorCount_one: "{{count}} Investor segment",
		investorCount_other: "{{count}} Investor segments",
		addProspect: "Add prospect segment",
		addPartner: "Add partner segment",
		nothingYet: "Nothing configured yet.",
		segmentActive: "Segment active",
		segmentInactive: "Segment deactivated",
		descriptionPlaceholder:
			"Which companies belong here — what they do, how big they are, what you can see about them from the outside.",
		personasInSegment: "Personas in this segment",
		personasAndScoring: "Personas and the Lead Scoring applied",
		noPersonaYet: "No persona activated yet",
		saveFirst: "Save the segment first, then activate personas in it.",
		createPersona: "Create new persona",
		deleteTitle: "Delete \u201c{{name}}\u201d permanently?",
		deleteBody:
			"It is linked to {{personas}} persona(s). This cannot be undone. If you only want to stop scoring it for now, deactivate it instead — that keeps everything and can be reversed.",
	},
	detail: {
		deactivated: "Deactivated — not used for scoring",
		rename: "Rename",
		deletePermanently: "Delete permanently",
		deactivateInstead: "Deactivate instead",
		issuesBefore_one: "{{count}} issue to fix before {{subject}} can be scored",
		issuesBefore_other: "{{count}} issues to fix before {{subject}} can be scored",
		issues: "Issues",
		noDescriptionYet: "No description yet.",
		clickToEdit: "Click to edit",
		personaColumn: "Persona",
		segmentColumn: "Segment",
		typeColumn: "Type",
	},
	personas: {
		intro: "Describe the people you sell to",
		subject: "these personas",
		test: "Test personas",
		testTitle: "Test your persona segments",
		count_one: "{{count}} Persona",
		count_other: "{{count}} Personas",
		countInfo:
			"A persona is a group of job titles that share the same problem. Signalist reads the functional description first and treats example titles as reinforcement. Two to eight personas works best.",
		add: "Add persona",
		personaActive: "Persona active",
		personaInactive: "Persona deactivated",
		descriptionPlaceholder:
			"What these people are responsible for, what they struggle with, and what they look for in a partner.",
		includeLabel: "Example job titles to include",
		includeInfo:
			"A handful of real titles is enough — Signalist generalises from them rather than matching them literally, and recognises the same roles in other languages.",
		excludeLabel: "Example job titles to exclude",
		excludeInfo:
			"A handful of real titles is enough. Anything close to them is excluded too, in any language Signalist encounters.",
		clash: "{{titles}} appears in both lists. Exclude wins, so these titles never match.",
		segmentsOfPersona: "Segments this persona exists in",
		segmentsAndScoring: "Segments and the Lead Scoring applied",
		noSegmentYet: "Not activated in any segment yet",
		deactivatedNote: "Currently deactivated. Nothing is graded with this persona.",
		saveFirst: "Save the persona first, then activate it in segments.",
		deleteTitle: "Delete \u201c{{name}}\u201d?",
		deleteBody:
			"It exists in {{segments}} segment(s). People who only matched this persona stop being scored. If you only want to pause it, deactivate it instead.",
		noResults:
			"No categorized companies available yet. Test your company segments first so there are classified companies to draw people from.",
	},
	scoringPage: {
		subject: "Lead Scorings",
		addAiCompany: "About the company",
		addAiPerson: "About the person",
		aiCompany: "AI · company",
		aiPerson: "AI · person",
		bucketPlaceholder: "Describe what gets {{tier}}\u2026",
		duplicateBucket:
			"This bucket says the same as another one, so the AI cannot tell them apart. State a different consequence for each bucket.",
		unassignedIntro: "Not graded:",
		// PREC-4127: a company-size range in no tier scores 0 points for a size the
		// engine did find, which is the easiest thing here to leave open by accident.
		sizeUnassigned_one:
			"{{ranges}} is not assigned to any tier, so leads that size score 0 points.",
		sizeUnassigned_other:
			"{{count}} ranges are not assigned to any tier, so leads those sizes score 0 points: {{ranges}}.",
		saved: "Saved.",
		intro:
			"Define what makes a good lead and which scoring applies to which company/persona combination",
		add: "Add Lead Scoring",
		newName: "New Lead Scoring",
		copyName: "{{name}} (copy)",
		scoringActive: "Lead Scoring active",
		scoringInactive: "Lead Scoring deactivated",
		copyFrom: "Copy the scores from a different Lead Scoring",
		copyOnly: "Only {{criterion}}",
		copyAll: "All criteria",
		copyFromTitle: "Copy the {{criterion}} scores from",
		copyAllTitle: "Replace every criterion with the scores from",
		copyNoOthers: "No other Lead Scoring yet",
		copyDefinitions_one: "{{count}} definition",
		copyDefinitions_other: "{{count}} definitions",
		copyValues_one: "{{count}} value",
		copyValues_other: "{{count}} values",
		copyCriteria_one: "{{count}} criterion",
		copyCriteria_other: "{{count}} criteria",
		copyNotPresent: "not present",
		duplicate: "Duplicate",
		deleteTitle: "Delete \u201c{{name}}\u201d?",
		deleteBodyApplied:
			"It is applied to {{count}} combination(s). Those fall back to no scoring and their leads stop being graded.",
		deleteBodyUnused: "It is not applied anywhere, so nothing else changes.",
		deactivatedBanner:
			"This Lead Scoring is deactivated. It grades nothing, and every combination pointing at it is flagged as an issue until you activate it again or pick another Lead Scoring.",
		missingBanner: "{{name}} Lead Scoring is missing {{criteria}}.",
		notAppliedBanner:
			"This Lead Scoring is not applied to any combination yet, so it does not grade anything.",
		addAiCriterion: "Add AI criterion",
		addAiCriterionTip:
			"Anything an AI agent can establish through web research can be graded here — how a company positions itself, whether it runs its own engineering, what its funding stage is, how mature its product looks. You write one definition for what should score higher and one for what should be disqualified.",
		aiCriterionName: "New AI criterion",
		criterionActive: "Criterion active",
		criterionInactive: "Criterion deactivated",
		deleteCriterion: "Delete criterion",
		activateTitle:
			"Activate the criterion \u201c{{criterion}}\u201d for the \u201c{{scoring}}\u201d Lead Scoring?",
		activateBody:
			"It is switched off at the moment, so nothing you change here would grade a lead until you activate it.",
		activateAndContinue: "Activate and continue",
		leaveOff: "Change it but leave it off",
		ifNotFound: "If we can\u2019t find this information for a lead or company",
		ifNotFoundIgnore: "Score 0 points for this criterion",
		ifNotFoundDisqualify: "Disqualify the lead",
		useHqBuckets: "Use the Company HQ buckets",
		scoreHigher: "Score higher",
		disqualify: "Disqualify",
		scoreHigherPlaceholder: "Describe the company that should score higher\u2026",
		disqualifyPlaceholder: "Describe the company that should be disqualified\u2026",
		tierPlaceholder: "Add\u2026",
		tierDisqualifyPlaceholder: "Nothing disqualifies \u2014 add to change that",
		unassigned_one: "{{count}} level is in no bucket and scores nothing",
		unassigned_other: "{{count}} levels are in no bucket and score nothing",
		inTier: "in {{tier}}",
		appliesTitle: "Define where the {{name}} Lead Scoring applies",
		appliesCount_one: "{{count}} combination uses this Lead Scoring",
		appliesCount_other: "{{count}} combinations use this Lead Scoring",
		stillNone: "{{missing}} of {{live}} combinations still have none",
		none: "No Lead Scoring selected.",
		criteria: {
			HQ_COUNTRY: "Company HQ",
			SUBSIDIARY_COUNTRY: "Any company location",
			INDUSTRY: "Industries",
			EMPLOYEE_SIZE: "Company size",
			PERSON_LOCATION: "Person location",
			JOB_LEVEL: "Seniority",
		},
		tiers: {
			3: "+3 points",
			2: "+2 points",
			1: "+1 point",
			0: "Disqualify",
		},
		conflict: {
			checkingTitle: "Analyzing your changes",
			checkingBody:
				"Checking that regions, countries and industries do not overlap across buckets.",
			running: "Running the conflict check\u2026",
			noneTitle: "Everything checks out.",
			noneBody: "No conflicts found. Your changes are saved and apply to the next enrichment run.",
			foundTitle_one: "{{count}} conflict found",
			foundTitle_other: "{{count}} conflicts found",
			foundBody:
				"A value sits in two buckets, so the scoring engine would grade it differently from run to run. Fix them, or save anyway.",
			partial:
				"The AI part of the check could not run; free-text overlaps may be missing from this list.",
			entry: "{{entry}} ({{tier}})",
			fix: "Fix",
			saveAnyway: "Save anyway",
			done: "Done",
			failed: "The check could not run. Your changes were not saved.",
		},
	},
	changeLog: {
		searchPlaceholder: "Search leads\u2026",
		lead: "Lead",
		date: "Date",
		previous: "Previous",
		updated: "Updated",
		userNote: "User note",
		updatedBy: "Updated by",
		empty: "No changes recorded yet.",
		company: "Company",
		persona: "Persona",
		showMore: "Show more",
		showLess: "Show less",
		viaList: "via List \u00b7 {{name}}",
		viaContacts: "via Contacts",
		priority: {
			priority_1: "Priority 1",
			priority_2: "Priority 2",
			priority_3: "Priority 3",
			disqualified: "Disqualified",
			unknown: "Unknown",
		},
	},
	loading: {
		intro: "Your Lead Scoring is being created",
	},
	how: {
		title: "How the Lead Score gets determined",
		body: "Every criterion you switch on feeds one of two totals. The company criteria — location, size, industry and the AI research — add up to the <b>company</b> score, the persona criteria to the <b>person</b> score. Each lands in a third of what its own criteria could award at most, and the two totals added together decide the overall Lead Score you filter on. A criterion that disqualifies on either side disqualifies the lead.",
		axisCompany: "Company score ↓",
		axisPersona: "Persona score →",
		rangeNote:
			"Where both sides agree, the Lead Score is that grade. Where they differ it lands between them, and exactly where depends on how many points each side collected — the totals are added, not the two grades.",
		example: {
			title: "An example",
			company: "Company score",
			persona: "Person score",
			overall: "Lead Score",
			hq: "Company HQ — Germany",
			size: "Company size — 50 to 200",
			ai: "AI research — runs its own engineering",
			seniority: "Seniority — Team Lead",
			location: "Person location — outside your markets",
			total: "{{points}} of {{max}} possible points",
			sum: "{{company}} + {{persona}} = {{points}} of {{max}} possible points",
			bands: "Grade borders",
			bandsMath:
				"Each side's own thirds, added: {{companyP3}} + {{personaP3}} and {{companyP2}} + {{personaP2}}.",
			note: "A company at {{company}} with a person at {{persona}} is the {{company}}–{{persona}} cell above. Here the company criteria can award more points than the persona criteria (9 against 6) and almost all of them landed, so the lead comes out {{overall}} rather than {{persona}}.",
		},
	},
	shell: {
		readHelpArticle: "Read the Help Center article",
		saveChanges: "Save changes",
		discardChanges: "Discard changes",
		saveAndLeave: "Save changes",
		discardAndLeave: "Discard changes",
		leaveBody:
			"Save them before you leave this screen, or discard them and go back to the last saved version.",
		issuesToFix_one: "{{count}} issue to fix",
		issuesToFix_other: "{{count}} issues to fix",
		clickToJump: "Click one to jump straight to it.",
		untitled: "Untitled",
		blankRowError: "The {{row}} at position {{position}} has no name. Name it before you save.",
		blankRowKind: {
			PROSPECT: "prospect segment",
			PARTNER: "partner segment",
			PERSONA: "persona",
		},
		titleTooLongError:
			'The persona "{{persona}}" has a job title over {{limit}} characters ("{{title}}"). The people search refuses it: shorten it before you save.',
		titleTooLongInline_one:
			"{{count}} job title is over {{limit}} characters. The people search refuses it, so shorten or split it.",
		titleTooLongInline_other:
			"{{count}} job titles are over {{limit}} characters. The people search refuses them, so shorten or split them.",
		comingSoon: "This tab is being rebuilt and lands with the next step of the redesign.",
		issues: {
			generalAboutMissing: "Describe your company under General settings",
			segmentNoName: "This segment has no name yet",
			segmentNoDescription: "No description text for {{name}}",
			segmentNoPersona: "No persona activated for {{name}}",
			segmentMissingScoring: "{{name}} has a missing Lead Scoring for {{persona}}",
			segmentInactiveScoring: "{{name}} uses {{scoring}} for {{persona}}, which is deactivated",
			personaNoName: "This persona has no name yet",
			personaNoDescription: "No functional description for {{name}}",
			personaNoTitles: "No example job titles for {{name}}",
			personaNotLinked: "{{name}} is not activated in any segment",
			personaMissingScoring: "{{name}} has a missing Lead Scoring in {{segment}}",
			personaInactiveScoring: "{{name}} uses {{scoring}} in {{segment}}, which is deactivated",
			scoringSizeUnassigned:
				"{{name}}: {{ranges}} is not assigned to any tier, so leads of that size score 0 points",
			scoringNotApplied: "{{name}}: not applied to any combination yet",
			scoringNoValues: "{{name}}: {{criterion}} has no values set",
			scoringAiEmpty: "{{name}}: {{criterion}} has no definitions yet",
			cellMissing: "{{segment}} has a missing Lead Scoring for {{persona}}",
		},
	},
};
